import type {
  CreateWebinarRequest,
  ImportAttendeesResult,
  PushLeadsResult,
  UpdateWebinarRequest,
  Webinar,
  WebinarAttendeeImportPreview,
  WebinarContentKind,
  WebinarContentPiece,
  WebinarDetail,
  WebinarStep,
  WebinarSurveyQuestion,
  WebinarSurveyStats,
} from "@xop/dashboard-shared";

import { api, apiBlob } from "../../../lib/api.js";

/**
 * Client for `/api/webinars` — the Marketing ▸ Webinars five-step flow.
 *
 * Everything the browser needs for the wizard is here, including the file
 * downloads (step 3's exports and step 4's CSV), which go through `apiBlob`
 * so they carry the session bearer — a bare `<a download>` to an API path
 * would hit the auth gate and download a 401 body as a `.md` file.
 */

const PREFIX = "/api/webinars";

export const webinarsApi = {
  list: () => api<{ webinars: Webinar[] }>(PREFIX).then((r) => r.webinars),

  get: (id: number) => api<WebinarDetail>(`${PREFIX}/${id}`),

  create: (body: CreateWebinarRequest) =>
    api<{ webinar: Webinar }>(PREFIX, { method: "POST", body }).then((r) => r.webinar),

  update: (id: number, body: UpdateWebinarRequest) =>
    api<{ webinar: Webinar }>(`${PREFIX}/${id}`, { method: "PATCH", body }).then(
      (r) => r.webinar,
    ),

  remove: (id: number) => api<{ ok: true }>(`${PREFIX}/${id}`, { method: "DELETE" }),

  setStep: (id: number, step: WebinarStep, done: boolean) =>
    api<{ webinar: Webinar }>(`${PREFIX}/${id}/steps`, {
      method: "POST",
      body: { step, done },
    }).then((r) => r.webinar),

  // ── Step 1 ────────────────────────────────────────────────────────
  /** Attach a campaign the CRM editor already created to a webinar slot. */
  linkCampaign: (id: number, slot: "invite" | "thankyou" | "survey", campaignId: number | null) =>
    api<{ webinar: Webinar }>(`${PREFIX}/${id}/link-campaign`, {
      method: "POST",
      body: { slot, campaign_id: campaignId },
    }).then((r) => r.webinar),

  // ── Step 2 ────────────────────────────────────────────────────────
  previewAttendees: (id: number, text: string) =>
    api<WebinarAttendeeImportPreview>(`${PREFIX}/${id}/attendees/preview`, {
      method: "POST",
      body: { text },
    }),

  importAttendees: (id: number, text: string) =>
    api<ImportAttendeesResult>(`${PREFIX}/${id}/attendees`, {
      method: "POST",
      body: { text },
    }),

  removeAttendee: (id: number, attendeeId: number) =>
    api<{ ok: true }>(`${PREFIX}/${id}/attendees/${attendeeId}`, { method: "DELETE" }),

  createThankYouCampaign: (id: number) =>
    api<{ webinar: Webinar; campaign_id: number; recipients: number }>(
      `${PREFIX}/${id}/thankyou-campaign`,
      { method: "POST" },
    ),

  // ── Step 3 ────────────────────────────────────────────────────────
  generateContent: (
    id: number,
    body: { kind: WebinarContentKind; hint?: string; with_image?: boolean },
  ) =>
    api<{ content: WebinarContentPiece; image_available: boolean }>(`${PREFIX}/${id}/content`, {
      method: "POST",
      body,
    }),

  getContent: (id: number, contentId: number) =>
    api<{ content: WebinarContentPiece }>(`${PREFIX}/${id}/content/${contentId}`).then(
      (r) => r.content,
    ),

  removeContent: (id: number, contentId: number) =>
    api<{ ok: true }>(`${PREFIX}/${id}/content/${contentId}`, { method: "DELETE" }),

  exportContent: (id: number, contentId: number, format: "md" | "html" | "txt" | "png") =>
    apiBlob(`${PREFIX}/${id}/content/${contentId}/export?format=${format}`),

  // ── Step 4 ────────────────────────────────────────────────────────
  publishSurvey: (id: number, questions?: WebinarSurveyQuestion[]) =>
    api<{ webinar: Webinar; survey: WebinarSurveyStats }>(`${PREFIX}/${id}/survey`, {
      method: "POST",
      body: questions ? { questions } : {},
    }),

  exportSurvey: (id: number) => apiBlob(`${PREFIX}/${id}/survey/export`),

  createSurveyCampaign: (id: number) =>
    api<{ webinar: Webinar; campaign_id: number; recipients: number }>(
      `${PREFIX}/${id}/survey-campaign`,
      { method: "POST" },
    ),

  // ── Step 5 ────────────────────────────────────────────────────────
  scoreLeads: (id: number) =>
    api<{ scored: number; of: number; warnings?: string[]; attendees: WebinarDetail["attendees"] }>(
      `${PREFIX}/${id}/score-leads`,
      { method: "POST" },
    ),

  pushLeads: (id: number, attendeeIds: number[]) =>
    api<PushLeadsResult>(`${PREFIX}/${id}/push-leads`, {
      method: "POST",
      body: { attendee_ids: attendeeIds },
    }),

  skipLeads: (id: number, attendeeIds: number[]) =>
    api<{ attendees: WebinarDetail["attendees"] }>(`${PREFIX}/${id}/skip-leads`, {
      method: "POST",
      body: { attendee_ids: attendeeIds },
    }),
};

/** Trigger a browser download of a blob the API returned. Kept here so every
 * export button in the wizard downloads identically. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking immediately can cancel the download in some browsers; a tick
  // later is enough for the click to have been taken.
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}
